// ============================================================================
//  dAdmin  ›  dAttendance  ›  shared building blocks
//
//  Small pieces used by the Attendance Manager page (dAttendance_Manage): the
//  work-pattern helpers + day picker, and the reminder status/format helpers +
//  modal/preview components. Kept here so there is one source of truth now that
//  the standalone Work Pattern / Reminders pages have been folded into the one
//  combined page.
// ============================================================================
import React, { useState, useEffect } from "react";
import { apiFetch } from "./utils/api";

export const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Only two base weeks are offered. Adhoc working days layer on top of either.
export const PATTERN_OPTIONS = ["MON_FRI", "MON_SAT"];
export const PATTERN_LABEL = {
    MON_FRI:   "Mon – Fri",
    MON_SAT:   "Mon – Sat",
    MON_ADHOC: "Mon – Fri",          // legacy value renders as its base week
};
export const PATTERN_HINT = {
    MON_FRI:   "Saturday and Sunday are week-offs.",
    MON_SAT:   "Only Sunday is a week-off.",
};

// Legacy MON_ADHOC rows are just Mon–Fri plus adhoc days; show them as MON_FRI.
export const normPattern = (p) => (p === "MON_ADHOC" ? "MON_FRI" : p);

// The base type of a date under a given pattern, used both when pruning adhoc
// days on a pattern switch and by the day picker. Mirrors the engine's ordering
// (week-off before holiday) so the preview matches the saved sheet.
export const baseTypeFor = (day, pattern) => {
    const weekendDow = pattern === "MON_SAT" ? [0] : [0, 6];
    if (weekendDow.includes(day.dow)) return "WEEKOFF";
    if (day.day_type === "HOLIDAY" ||
        (day.adhoc && day.label.includes("overrides") && !day.label.includes("week-off"))) {
        return "HOLIDAY";
    }
    return "WORK";
};

// react-select styling — mirrors Manager Allocation's filter dropdowns.
export const SELECT_STYLES = {
    container: (p) => ({ ...p, minWidth: 170 }),
    control: (p, s) => ({
        ...p, borderRadius: 8, minHeight: 38,
        borderColor: s.isFocused ? "#E8520A" : "#e2e8f0",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(232, 82, 10, 0.12)" : "none",
        "&:hover": { borderColor: s.isFocused ? "#E8520A" : "#cbd5e1" },
    }),
    option: (p, s) => ({
        ...p, backgroundColor: s.isSelected ? "#E8520A" : s.isFocused ? "#FFF5F0" : "white",
        color: s.isSelected ? "#fff" : "#0f172a", padding: "9px 12px", fontSize: 13,
    }),
    placeholder: (p) => ({ ...p, color: "#94a3b8", fontSize: 13 }),
    singleValue: (p) => ({ ...p, color: "#0f172a", fontSize: 13 }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
};

// Sheet-status → [pill tone, label].
export const STATUS_PILL = {
    draft:          ["amber", "Draft"],
    saved:          ["amber", "Saved"],
    submitted:      ["green", "Submitted"],
    edit_requested: ["orange", "Edit requested"],
    edit_open:      ["orange", "Edit open"],
    approved:       ["green", "Approved"],
    rejected:       ["red", "Rejected"],
    not_started:    ["grey", "Not started"],
    completed:      ["navy", "Completed"],
};

export const SUBMITTED = ["submitted", "approved", "edit_requested", "edit_open"];

// Compact "when the reminder went out" label, e.g. "08 Sep, 02:30 pm".
export const fmtSentAt = (ts) =>
    new Date(ts).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });

// ---------------------------------------------------------------------------
// The day grid under an expanded row. Every week-off or holiday is clickable to
// promote it to an adhoc working day; days already working under the base
// pattern are a read-only preview.
// ---------------------------------------------------------------------------
export function DayPicker({ emp, cfg, onToggle }) {
    const picked = new Set(cfg.adhoc_days);

    // emp.days is the server's calendar for the SAVED pattern; baseTypeFor
    // recomputes the base type against the draft pattern so the preview (and
    // which days can be picked) follows the dropdown immediately.
    const overridesHoliday = cfg.adhoc_days.some((d) => {
        const day = emp.days.find((x) => x.date === d);
        return day && baseTypeFor(day, cfg.pattern) === "HOLIDAY";
    });

    return (
        <div className="datt-daypicker">
            <div className="datt-daypicker-head">
                <span>
                    <strong>{emp.emp_name}</strong> - {PATTERN_HINT[cfg.pattern]}
                    <em> Click any week-off or holiday to make it an adhoc working day.</em>
                </span>
            </div>

            <div className="datt-daygrid">
                {emp.days.map((day) => {
                    const type = baseTypeFor(day, cfg.pattern);
                    const isPicked = picked.has(day.date);
                    // Adhoc only turns a NON-working day (week-off or holiday) into a
                    // working one. A day that is already a working day under the base
                    // pattern can't be made "adhoc" — so it stays non-clickable.
                    const canToggle = type !== "WORK";
                    const cls = isPicked ? "is-picked"
                        : type === "HOLIDAY" ? "is-holiday"
                        : type === "WEEKOFF" ? "is-off" : "is-work";
                    return (
                        <button
                            key={day.date}
                            type="button"
                            className={`datt-day ${cls}${canToggle ? " is-clickable" : ""}`}
                            disabled={!canToggle}
                            title={
                                isPicked ? `Adhoc working day (${day.label})`
                                : type === "WORK" ? `${day.label} - already a working day`
                                : `${day.label} - click to make it a working day`
                            }
                            onClick={() => canToggle && onToggle(day.date)}
                        >
                            <span className="datt-day-dow">{DOW[day.dow]}</span>
                            <span className="datt-day-num">{day.date.slice(8)}</span>
                            {isPicked && type !== "WORK" && (
                                <span className="datt-day-ovr">{type === "HOLIDAY" ? "OVR HOL" : "OVR OFF"}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="datt-legend">
                <span><i className="sw is-work" />Working</span>
                <span><i className="sw is-off" />Week-off</span>
                <span><i className="sw is-holiday" />Holiday (from dTime)</span>
                <span><i className="sw is-picked" />Adhoc working day</span>
            </div>

            {overridesHoliday && (
                <div className="datt-notice is-amber" style={{ marginTop: 10, marginBottom: 0 }}>
                    This employee is being asked to work on a declared holiday. Confirm it is intentional before saving.
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// A modal shell used by the reminder preview / bulk / history dialogs.
// ---------------------------------------------------------------------------
export function Modal({ title, children, onClose, foot, wide }) {
    return (
        <div className="datt-modal-backdrop" onClick={onClose}>
            <div className={`datt-modal${wide ? " is-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
                <div className="datt-modal-head">
                    <span className="datt-modal-title">{title}</span>
                    <button type="button" className="datt-btn is-quiet is-sm" onClick={onClose}>Close</button>
                </div>
                <div className="datt-modal-body">{children}</div>
                {foot && <div className="datt-modal-foot">{foot}</div>}
            </div>
        </div>
    );
}

// The rendered reminder email — the same HTML the server will send.
export function MailPreview({ mail, employee }) {
    return (
        <div>
            <div className="datt-mail-meta">
                <span>To</span><span>{mail.to || "no email on file"}</span>
                <span>From</span><span>{mail.from}</span>
                <span>Subject</span><span className="datt-strong">{mail.subject}</span>
                <span>Due</span><span>{mail.dueDate}</span>
            </div>
            {/* The server built this HTML and will send exactly it. Rendering
                the same string here is the whole point of the preview. */}
            <div className="datt-mail-body" dangerouslySetInnerHTML={{ __html: mail.html }} />
            {employee && !mail.to && (
                <div className="datt-notice is-red" style={{ marginTop: 12, marginBottom: 0 }}>
                    {employee.emp_name} has no email address in dAdmin - this send will be skipped.
                </div>
            )}
        </div>
    );
}

// Preview of one employee's reminder from inside the bulk list.
export function BulkPeek({ empId, which, year, month, onBack }) {
    const [payload, setPayload] = useState(null);
    useEffect(() => {
        (async () => {
            const res = await apiFetch(
                `/api/dattendance/reminders/preview?emp_id=${encodeURIComponent(empId)}&year=${year}&month=${month}&which=${which}`);
            const json = await res.json();
            if (res.ok) setPayload(json);
        })();
    }, [empId, which, year, month]);

    return (
        <Modal title={`Reminder ${which} preview`} onClose={onBack}
            foot={<button type="button" className="datt-btn is-ghost" onClick={onBack}>Back to list</button>}>
            {payload ? <MailPreview mail={payload.mail} employee={payload.employee} />
                     : <p className="datt-modal-note">Loading…</p>}
        </Modal>
    );
}
